import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import intakeAPI from '../../api/intake';
import { API_URL } from '../../utils/apiConfig';
import { getApiErrorMessage } from '../../utils/errorUtils';
import { formatFailedActionMessage } from '../../utils/uiMessages';

const IntakeArchive = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    attached: '',
  });
  const [attachDialogOpen, setAttachDialogOpen] = useState(false);
  const [selectedIntake, setSelectedIntake] = useState(null);
  const [patientSearch, setPatientSearch] = useState('');
  const [patientOptions, setPatientOptions] = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [attaching, setAttaching] = useState(false);

  const loadArchive = async () => {
    try {
      setLoading(true);
      const payload = {
        search: filters.search || undefined,
        status: filters.status || undefined,
        attached: filters.attached || undefined,
        limit: 100,
      };
      const response = await intakeAPI.getArchive(payload);
      setItems(response?.data || []);
      setError('');
    } catch (err) {
      setError(getApiErrorMessage(err, formatFailedActionMessage('load', 'intake archive')));
    } finally {
      setLoading(false);
    }
  };

  const fetchPatients = async (term = '') => {
    try {
      const token = localStorage.getItem('token');
      const query = new URLSearchParams({
        page: '1',
        limit: '50',
      });
      if (term.trim()) query.set('search', term.trim());
      const res = await axios.get(`${API_URL}/patients?${query.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPatientOptions(res?.data?.data || []);
    } catch (err) {
      setPatientOptions([]);
    }
  };

  useEffect(() => {
    loadArchive();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.status, filters.attached]);

  useEffect(() => {
    if (!attachDialogOpen) return;
    const timeout = setTimeout(() => {
      fetchPatients(patientSearch);
    }, 300);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientSearch, attachDialogOpen]);

  const handleOpenIntake = (item) => {
    if (item?.attached && item?.patient?.id && item?.intakeId) {
      navigate(`/patients/${item.patient.id}/intakes/${item.intakeId}`);
      return;
    }
    if (item?.token) {
      window.open(`/intake/${item.token}`, '_blank');
    }
  };

  const openAttachDialog = (item) => {
    setSelectedIntake(item);
    setSelectedPatientId('');
    setPatientSearch('');
    setPatientOptions([]);
    setAttachDialogOpen(true);
  };

  const handleAttach = async () => {
    if (!selectedIntake?.intakeId || !selectedPatientId) return;
    try {
      setAttaching(true);
      await intakeAPI.attachToPatient(selectedIntake.intakeId, selectedPatientId);
      toast.success('Intake attached to patient');
      setAttachDialogOpen(false);
      setSelectedIntake(null);
      await loadArchive();
    } catch (err) {
      toast.error(getApiErrorMessage(err, formatFailedActionMessage('attach', 'intake')));
    } finally {
      setAttaching(false);
    }
  };

  const rows = useMemo(() => items || [], [items]);

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h4">Intake Archive</Typography>
      </Box>

      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Search (name/phone/ID)"
            value={filters.search}
            onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
            sx={{ minWidth: 260 }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={filters.status}
              onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="submitted">Submitted</MenuItem>
              <MenuItem value="reviewed">Reviewed</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Attached</InputLabel>
            <Select
              label="Attached"
              value={filters.attached}
              onChange={(e) => setFilters((prev) => ({ ...prev, attached: e.target.value }))}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Attached</MenuItem>
              <MenuItem value="false">Not attached</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={() => setFilters({ search: '', status: '', attached: '' })}>
            Reset
          </Button>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper elevation={2}>
        {loading ? (
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Submitted</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>ID</TableCell>
                  <TableCell>Full Name</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Risk</TableCell>
                  <TableCell>Attached Patient</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.intakeId}>
                    <TableCell>{row.submittedAt ? new Date(row.submittedAt).toLocaleString() : '—'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={row.status} color={row.status === 'reviewed' ? 'success' : row.status === 'submitted' ? 'primary' : 'default'} />
                    </TableCell>
                    <TableCell>{row.answers?.patientRecordId || '—'}</TableCell>
                    <TableCell>{row.answers?.fullName || row.lead?.fullName || '—'}</TableCell>
                    <TableCell>{row.answers?.phone || row.lead?.phone || '—'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={`${row.riskScore || 0}`} color={row.riskScore > 0 ? 'warning' : 'default'} />
                    </TableCell>
                    <TableCell>{row.patient?.name ? `${row.patient.name} (${row.patient.patientId || row.patient.id})` : '—'}</TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => handleOpenIntake(row)}>Open</Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => openAttachDialog(row)}
                      >
                        Attach
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No intakes found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={attachDialogOpen} onClose={() => setAttachDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Attach intake to patient</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Intake: {selectedIntake?.answers?.fullName || selectedIntake?.lead?.fullName || selectedIntake?.intakeId}
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="Search patient"
            value={patientSearch}
            onChange={(e) => setPatientSearch(e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth size="small">
            <InputLabel>Select patient</InputLabel>
            <Select
              value={selectedPatientId}
              label="Select patient"
              onChange={(e) => setSelectedPatientId(e.target.value)}
            >
              {patientOptions.map((p) => (
                <MenuItem key={p._id} value={p._id}>
                  {p.name || 'Unnamed'} ({p.patientId || p._id}) {p.phone ? `- ${p.phone}` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAttachDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAttach} variant="contained" disabled={!selectedPatientId || attaching}>
            {attaching ? 'Attaching...' : 'Attach'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default IntakeArchive;
